import React, { useState } from 'react';
import * as translator from '../../../../../utils/translator';
import { formatDateStamp } from '../../../../../utils/timestampFormatter';
import { WeightPoint } from 'data-types';
import CareProfileCard from './CareProfileCard';
import CareProfileGradientButton from './CareProfileGradientButton';
import { localeByLanguage } from '../careProfileAssets';
import { fillWeight, WeightUnit } from '../../../../../utils/weightUnits';

interface WeightTrendChartProps {
	series: WeightPoint[];
	goalKg: number | null;
	language: string;
	onLogWeight: () => void;
	weightUnit?: WeightUnit;
}


const CHART_WIDTH = 320;
const CHART_HEIGHT = 140;
const PADDING_X = 12;
const PADDING_Y = 16;

// Keep the tooltip inside the card: right-align near the right edge, left-align near the left edge.
const tooltipShiftX = (xPct: number): string => {
	if (xPct > 66) return '-92%';
	if (xPct < 34) return '-8%';
	return '-50%';
};

const WeightTrendChart: React.FC<WeightTrendChartProps> = ({ series, goalKg, language, onLogWeight, weightUnit = 'kg' }) => {
	const hasChart = series.length >= 2;

	return (
		<CareProfileCard id="weight-trend-chart" className="relative border border-solid border-[#e7e4db] rounded-[20px]">
			<div id="weight-trend-chart-header" className="mb-2">
				<p className="text-[22px] font-medium text-care-plus-heading leading-tight">{translator.translate(language, 'my_account', 'CareProfile/Chart/Title')}</p>
				<p className="text-[13px] text-[#99a3b2] mt-0.5">{translator.translate(language, 'my_account', 'CareProfile/Chart/Subtitle')}</p>
			</div>

			{hasChart ? (
				<WeightTrendChartBody series={series} goalKg={goalKg} language={language} weightUnit={weightUnit} />
			) : (
				<div id="weight-trend-chart-empty" className="flex items-center justify-center h-[180px] text-center">
					<p className="text-sm text-[#99a3b2] max-w-[220px]">{translator.translate(language, 'my_account', 'CareProfile/Chart/Empty')}</p>
				</div>
			)}

			<div id="weight-trend-chart-log-weight-button" className="mt-5">
				<CareProfileGradientButton onClick={onLogWeight} rounded="rounded-[12px]" className="py-4 text-[18px]">
					{translator.translate(language, 'my_account', 'CareProfile/WeightProgress/LogWeight')}
				</CareProfileGradientButton>
			</div>
		</CareProfileCard>
	);
};

interface WeightTrendChartBodyProps {
	series: WeightPoint[];
	goalKg: number | null;
	language: string;
	weightUnit: WeightUnit;
}

const WeightTrendChartBody: React.FC<WeightTrendChartBodyProps> = ({ series, goalKg, language, weightUnit }) => {
	const [activeIndex, setActiveIndex] = useState<number | null>(series.length - 1);

	const weights = series.map((point) => point.weightKg);
	const values = goalKg !== null ? [...weights, goalKg] : weights;
	const minValue = Math.min(...values);
	const maxValue = Math.max(...values);
	const valueRange = maxValue - minValue || 1;

	const toX = (index: number) => PADDING_X + (index / (series.length - 1)) * (CHART_WIDTH - PADDING_X * 2);
	const toY = (value: number) => PADDING_Y + (1 - (value - minValue) / valueRange) * (CHART_HEIGHT - PADDING_Y * 2);

	const points = series.map((point, index) => ({ x: toX(index), y: toY(point.weightKg) }));
	const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
	const baselineY = CHART_HEIGHT - PADDING_Y;
	const areaPath = `${linePath} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`;
	const goalY = goalKg !== null ? toY(goalKg) : null;
	const active = activeIndex !== null ? series[activeIndex] : null;
	const activePoint = activeIndex !== null ? points[activeIndex] : null;

	const dateLabel = (date: string) => formatDateStamp(date, { noDayString: true, showYear: false, useShortMonth: true, locale: localeByLanguage[language] || 'en-US' });
	const tooltipDateLabel = (date: string) => formatDateStamp(date, { noDayString: true, showYear: true, useShortMonth: true, locale: localeByLanguage[language] || 'en-US' });

	const rawTooltipWeightLabel = translator.translate(language, 'my_account', 'CareProfile/Chart/TooltipWeight');
	const [tooltipWeightBefore, tooltipWeightAfter] = rawTooltipWeightLabel.split('{{value}}');

	return (
		<>
			<svg
				id="weight-trend-chart-svg"
				viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
				className="w-full h-auto"
				preserveAspectRatio="none"
			>
				<defs>
					<linearGradient id="weight-trend-chart-fill" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="#f59603" stopOpacity={0.25} />
						<stop offset="100%" stopColor="#f59603" stopOpacity={0} />
					</linearGradient>
				</defs>
				{goalY !== null && (
					<line
						id="weight-trend-chart-goal-line"
						x1={PADDING_X}
						y1={goalY}
						x2={CHART_WIDTH - PADDING_X}
						y2={goalY}
						stroke="#99a3b2"
						strokeDasharray="4 4"
						strokeWidth={1}
					/>
				)}
				<path id="weight-trend-chart-area" d={areaPath} fill="url(#weight-trend-chart-fill)" stroke="none" />
				<path
					id="weight-trend-chart-line"
					d={linePath}
					fill="none"
					stroke="#f59603"
					strokeWidth={2}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				{points.map((point, index) => (
					<circle
						key={series[index].date}
						cx={point.x}
						cy={point.y}
						r={activeIndex === index ? 5 : 3}
						fill="#f59603"
						onClick={() => setActiveIndex(index)}
						style={{ cursor: 'pointer' }}
					/>
				))}
			</svg>

			{active && activePoint && (
				<div
					id="weight-trend-chart-tooltip"
					className="absolute flex flex-col gap-y-0.5 bg-[#fffdf8] border border-solid border-[#f59200] text-xs rounded-[13px] px-2.5 py-1.5 pointer-events-none shadow-sm whitespace-nowrap"
					style={{
						left: `${(activePoint.x / CHART_WIDTH) * 100}%`,
						top: `${(activePoint.y / CHART_HEIGHT) * 100}%`,
						transform: `translate(${tooltipShiftX((activePoint.x / CHART_WIDTH) * 100)}, -130%)`,
					}}
				>
					<span className="text-care-plus-heading">{tooltipDateLabel(active.date)}</span>
					<span
						className="font-medium bg-clip-text text-transparent"
						style={{ backgroundImage: 'linear-gradient(180deg, #ffb909 1.6768%, #e17200 94.034%)' }}
					>
						{weightUnit === 'kg'
							? <>{tooltipWeightBefore}{active.weightKg}{tooltipWeightAfter}</>
							: fillWeight(rawTooltipWeightLabel, '{{value}}', active.weightKg, weightUnit)}
					</span>
				</div>
			)}

			<div id="weight-trend-chart-x-axis" className="flex items-center justify-between mt-1">
				<p className="text-[11px] text-[#99a3b2] tracking-[-0.3px]">{dateLabel(series[0].date)}</p>
				<p className="text-[11px] text-[#99a3b2] tracking-[-0.3px]">{dateLabel(series[series.length - 1].date)}</p>
			</div>
		</>
	);
};

export default WeightTrendChart;
